import { useSearchParams } from "react-router-dom";
import type {
  RolloutCompare,
  RolloutCompareDelta,
  RolloutCompareLimitation,
  RolloutCompareSide,
  RolloutCompareUnknownReason,
  Section,
} from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { num, pct } from "@/lib/format";

/**
 * 보존 리비전 앵커 전후 HTTP 요청 비교 카드 — ADR 0053 · Issue #158
 * --------------------------------------------------------------------------
 * **조회 전용**입니다. 값은 `useWorkloadDetail`이 이미 받아 온 같은 응답의
 * `rolloutCompare` 섹션에서 옵니다 — 새 요청도, 새 훅도, 폴링도 만들지 않습니다.
 *
 * - 앵커 선택기의 `value`는 **서버가 발급한 `anchorId`**입니다. revision은 화면에
 *   따로 보여 줄 뿐 선택자가 아닙니다 — revision은 가변이고 유일하지도 않아서,
 *   RS가 지워지고 같은 이름·같은 revision으로 다시 생기면 같은 선택자가 다른
 *   객체를 가리킵니다.
 * - 목록에 없는 값을 UI가 스스로 만들어 보내는 경로를 만들지 않습니다.
 * - 선택은 URL(`?compareAnchor=`·`?compareWindow=`)에 둡니다. 자동 갱신이 선택을
 *   지우지 않고 북마크가 같은 화면을 엽니다.
 * - **인과를 말하지 않습니다.** 보여 주는 것은 "앵커 시각 앞뒤의 겹치지 않는
 *   등길이 두 창에서 각각 관측된 값과 그 차이"뿐입니다.
 * - 와이어는 **비율 원값**이고 `%p`·`%` 변환은 **이 포맷터만** 합니다.
 * - "데이터 없음 · 앵커 미선택 · 관측 부족 · 상류 장애"를 같은 빈 화면으로
 *   처리하지 않습니다.
 */

/**
 * 미상 사유 → 문구 id.
 *
 * 닫힌 집합을 **운영자의 행동**으로 묶습니다. 같은 행동을 요구하는 사유는 같은
 * 문장을 씁니다 — 닫힌 집합은 enum과 도달 가능성의 계약이지 "코드마다 다른 문장"의
 * 계약이 아닙니다. 서로 **다른 행동**을 요구하는 사유만 다른 문장을 갖습니다.
 *
 * 의미가 실제로 같은 곳에서는 **기존 SLO 문구를 그대로 재사용**합니다(원천 출처
 * 미검증·질의 계획 불일치·계약 위반 응답·결과 과대·상류 무응답·대상 미확정).
 * 달성률이나 Deployment/StatefulSet 같은 SLO 전용 표현이 든 문구는 쓰지 않습니다 —
 * 그 문장은 이 화면에서 사실이 아닙니다.
 *
 * 템플릿으로 id를 조립하지 않습니다 — 조립하면
 * 카탈로그 게이트가 "쓰이지 않는 문구"로 읽고, 사유가 늘어도 아무도 모르게
 * 원문 코드가 화면에 나갑니다. 이 표는 계약의 union을 전부 덮어야 타입 검사를
 * 통과합니다.
 */
const REASON_MESSAGE: Record<RolloutCompareUnknownReason, string> = {
  compare_not_configured: "common.rolloutCompare.reason.notConfigured",
  slo_not_configured: "common.rolloutCompare.reason.notConfigured",
  kind_unsupported: "common.rolloutCompare.reason.kindUnsupported",
  workload_uid_missing: "common.slo.reason.scopeUnsupported",
  scope_unsupported: "common.slo.reason.scopeUnsupported",
  no_anchors: "common.rolloutCompare.reason.noAnchors",
  inventory_scan_truncated: "common.rolloutCompare.reason.scanTruncated",
  anchor_not_selected: "common.rolloutCompare.reason.notSelected",
  anchor_unknown: "common.rolloutCompare.reason.anchorUnknown",
  anchor_deleted: "common.rolloutCompare.reason.anchorDeleted",
  anchor_ambiguous: "common.rolloutCompare.reason.anchorAmbiguous",
  anchor_missing_time: "common.rolloutCompare.reason.anchorTime",
  anchor_time_out_of_range: "common.rolloutCompare.reason.anchorTime",
  anchor_source_unavailable: "common.rolloutCompare.reason.sourceUnavailable",
  anchor_source_invalid: "common.rolloutCompare.reason.sourceUnavailable",
  anchor_revision_reused: "common.rolloutCompare.reason.anchorReused",
  anchor_too_recent: "common.rolloutCompare.reason.tooRecent",
  anchor_before_trusted_bound: "common.rolloutCompare.reason.beforeBound",
  namespace_bound_unknown: "common.rolloutCompare.reason.boundUnknown",
  workload_bound_unknown: "common.rolloutCompare.reason.boundUnknown",
  window_unsupported: "common.rolloutCompare.reason.windowUnsupported",
  window_clamped_too_short: "common.rolloutCompare.reason.windowUnsupported",
  source_not_configured: "common.slo.reason.sourceUnverified",
  source_provenance_unverified: "common.slo.reason.sourceUnverified",
  source_ingestion_unqualified: "common.rolloutCompare.reason.ingestionUnqualified",
  query_unsupported: "common.slo.reason.queryUnsupported",
  before_no_data: "common.rolloutCompare.reason.noData",
  after_no_data: "common.rolloutCompare.reason.noData",
  before_no_eligible_events: "common.rolloutCompare.reason.lowTraffic",
  after_no_eligible_events: "common.rolloutCompare.reason.lowTraffic",
  before_insufficient_coverage: "common.rolloutCompare.reason.coverage",
  after_insufficient_coverage: "common.rolloutCompare.reason.coverage",
  before_stale_data: "common.rolloutCompare.reason.stale",
  after_stale_data: "common.rolloutCompare.reason.stale",
  before_low_traffic: "common.rolloutCompare.reason.lowTraffic",
  after_low_traffic: "common.rolloutCompare.reason.lowTraffic",
  invalid_status_share: "common.rolloutCompare.reason.invalidStatusShare",
  invalid_sample: "common.slo.reason.invalidResponse",
  invalid_ratio: "common.slo.reason.invalidResponse",
  cardinality_exceeded: "common.slo.reason.tooLarge",
  result_too_large: "common.slo.reason.tooLarge",
  invalid_response: "common.slo.reason.invalidResponse",
  scope_mismatch: "common.slo.reason.invalidResponse",
  upstream_unavailable: "common.slo.reason.upstream",
  timeout: "common.slo.reason.upstream",
  circuit_open: "common.slo.reason.upstream",
};

/**
 * 상류 장애로 **볼 수 있는** 사유입니다. 나머지는 "기능이 꺼져 있다 · 앵커를
 * 고르지 않았다 · 표본이 모자라다" 같은 **사실**이지 장애가 아닙니다. 그것까지
 * 장애 배지로 칠하면 운영자가 멀쩡한 데이터소스를 의심합니다.
 */
const OUTAGE_REASONS: ReadonlySet<RolloutCompareUnknownReason> = new Set([
  "upstream_unavailable",
  "timeout",
  "circuit_open",
  "invalid_response",
  "scope_mismatch",
  "invalid_sample",
  "invalid_ratio",
  "result_too_large",
  "cardinality_exceeded",
]);

/** 한계 코드 → 문구 id. 계약과 UI가 **같은 한계**를 말합니다. */
const LIMITATION_MESSAGE: Record<RolloutCompareLimitation, string> = {
  increase_extrapolated: "common.rolloutCompare.limit.extrapolated",
  anchor_is_object_creation: "common.rolloutCompare.limit.objectCreation",
  reuse_absence_not_proof: "common.rolloutCompare.limit.reuseNotProof",
  unobserved_series_excluded: "common.rolloutCompare.limit.unobserved",
  inter_window_gap_excluded: "common.rolloutCompare.limit.gapExcluded",
  inventory_truncated: "common.rolloutCompare.limit.truncated",
  counter_resets_absorbed_by_increase: "common.rolloutCompare.limit.resets",
};

/**
 * 창별 사유는 **문구 하나 + 구간 이름 파라미터**입니다.
 *
 * `앞 구간에 관측이 없습니다`와 `뒤 구간에 관측이 없습니다`를 각각 문구로 두면
 * 같은 문장을 두 벌 유지하게 되고 번역 조각도 두 배가 됩니다. 구간 이름은 이미
 * 표 머리에서 쓰는 문구라 새로 만들지 않습니다.
 */
const WINDOW_OF: Partial<Record<RolloutCompareUnknownReason, string>> = {
  before_no_data: "common.rolloutCompare.beforeHeader",
  before_no_eligible_events: "common.rolloutCompare.beforeHeader",
  before_insufficient_coverage: "common.rolloutCompare.beforeHeader",
  before_stale_data: "common.rolloutCompare.beforeHeader",
  before_low_traffic: "common.rolloutCompare.beforeHeader",
  after_no_data: "common.rolloutCompare.afterHeader",
  after_no_eligible_events: "common.rolloutCompare.afterHeader",
  after_insufficient_coverage: "common.rolloutCompare.afterHeader",
  after_stale_data: "common.rolloutCompare.afterHeader",
  after_low_traffic: "common.rolloutCompare.afterHeader",
};

/** 앵커가 선택 불가일 때 옵션에 붙이는 짧은 사유입니다. */
const ANCHOR_NOTE: Partial<Record<RolloutCompareUnknownReason, string>> = {
  anchor_ambiguous: "common.rolloutCompare.note.ambiguous",
  anchor_revision_reused: "common.rolloutCompare.note.reused",
  anchor_missing_time: "common.rolloutCompare.note.noTime",
  anchor_time_out_of_range: "common.rolloutCompare.note.noTime",
  anchor_source_unavailable: "common.rolloutCompare.note.unavailable",
  anchor_source_invalid: "common.rolloutCompare.note.unavailable",
  anchor_unknown: "common.rolloutCompare.note.unavailable",
};

/**
 * 지표 → 문구 id.
 *
 * **템플릿으로 id를 조립하지 않습니다** — 조립하면 i18n 게이트가 "쓰이지 않는
 * 문구"로 읽고, 지표가 늘어도 아무도 모르게 원문 코드가 화면에 그대로 나갑니다.
 */
const METRIC_MESSAGE: Record<RolloutCompareDelta["metric"], string> = {
  requests: "common.rolloutCompare.metric.requests",
  errorRatio: "common.rolloutCompare.metric.errorRatio",
  requestsPerSecond: "common.rolloutCompare.metric.requestsPerSecond",
};

/** 사다리 이름은 서버가 정합니다. 브라우저가 값을 지어내지 않습니다. */
const WINDOW_OPTIONS = ["1h", "6h", "24h"] as const;

/** 초 단위 길이를 언어 중립 라벨로 바꿉니다(창 이름과 **같은 표기**). */
function windowLabel(seconds: number): string {
  if (seconds <= 0) return "0s";
  if (seconds % 3_600 === 0) return `${seconds / 3_600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
}

/** RFC3339를 화면 표기로 줄입니다. 원문 그대로 보여 주면 폭이 감당되지 않습니다. */
function clock(iso: string): string {
  return iso.replace("T", " ").replace("Z", "").replace(/\.\d+/, "");
}

/**
 * **와이어의 비율 원값을 퍼센트 포인트로 바꾸는 유일한 자리입니다.**
 *
 * 계약은 언제나 비율(`0.1`)을 싣고 화면만 `10%p`로 적습니다. 둘을 섞으면 10배
 * 오차가 조용히 지나갑니다.
 */
function ratioToPoints(v: number, digits = 2): string {
  return pct(v * 100, digits);
}

/** 부호를 **명시**합니다. `+`가 없으면 증가와 감소가 눈으로 구분되지 않습니다. */
function signed(text: string, v: number): string {
  return v > 0 ? `+${text}` : text;
}

/**
 * 값을 **같은 자리수 규칙**으로 적습니다.
 *
 * 두 가지를 동시에 지킵니다.
 *
 *  ① **0이 아닌 값을 `0`으로 보여 주지 않습니다.** `num`은 정수로 반올림하므로
 *     초당 요청처럼 값이 작은 지표에서는 실제 변화(+0.37/s)가 `+0`이 되어
 *     "변화 없음"으로 읽힙니다.
 *  ② **같은 행의 세 값이 서로 어긋나 보이지 않습니다.** 앞·뒤를 정수로 줄이고
 *     차이만 소수로 적으면 `2`, `3`, `+0.37`이 되어 표가 스스로를 반박합니다.
 *
 * 값을 바꾸는 것이 아니라 **보이는 자리수**만 정합니다 — 판정은 서버가 이미
 * 끝냈습니다.
 */
function amount(v: number): string {
  if (v === 0) return "0";
  /* 큰 값은 정수와 자리 구분자가 읽기 쉽습니다. */
  if (Math.abs(v) >= 100) return num(v);
  for (const digits of [2, 3, 4]) {
    const text = v.toFixed(digits);
    if (Number(text) !== 0) return String(Number(text));
  }
  /* 표시할 수 있는 자리수 안에서 0과 구분되지 않을 만큼 작은 값입니다.
     그 사실을 `0`이라고 단정하지 않고 "0에 가깝다"로 적습니다. */
  return v > 0 ? "≈0" : "≈-0";
}

/** 변화량 하나를 단위에 맞는 문자열로 적습니다. */
function deltaText(d: RolloutCompareDelta): string {
  switch (d.unit) {
    case "ratio":
      return signed(`${ratioToPoints(d.absolute)}p`, d.absolute);
    case "per_second":
      return signed(`${amount(d.absolute)}/s`, d.absolute);
    default:
      return signed(amount(d.absolute), d.absolute);
  }
}

/** 지표의 고정 순서입니다. `deltas`가 비어도 표가 무너지지 않게 합니다. */
const METRICS: RolloutCompareDelta["metric"][] = ["requests", "errorRatio", "requestsPerSecond"];

function sideValue(side: RolloutCompareSide | undefined, metric: RolloutCompareDelta["metric"]): string {
  if (!side) return "—";
  switch (metric) {
    case "errorRatio":
      return ratioToPoints(side.errorRatio);
    case "requestsPerSecond":
      return `${amount(side.requestsPerSecond)}/s`;
    default:
      return amount(side.requests);
  }
}

export function RolloutCompareCard({
  section,
  referenceIso,
}: {
  section?: Section<RolloutCompare>;
  referenceIso?: string;
}) {
  const { t } = useT("common");
  const [params, setParams] = useSearchParams();
  const data = section?.data;
  const reason = section?.status === "degraded" ? (section.reason as RolloutCompareUnknownReason) : undefined;
  const anchors = data?.inventory.anchors ?? [];
  const window = data?.window;
  const outage = reason !== undefined && OUTAGE_REASONS.has(reason);

  /** 선택은 URL에 둡니다. 빈 값은 키 자체를 지워 "서버 기본값"과 같은 요청이 됩니다. */
  const choose = (key: "compareAnchor" | "compareWindow", value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  /* 선택된 값은 **서버가 해석한 것**을 우선합니다. 서버가 해석하지 못했으면
     URL 원문을 그대로 두어 선택이 화면에서 사라지지 않게 합니다. */
  const selectedAnchor = data?.selectedAnchorId ?? params.get("compareAnchor") ?? "";
  const selectedWindow = window?.requestedName ?? params.get("compareWindow") ?? "";

  /* 비교가 **성사된 것**은 섹션이 ok이고 두 창의 값과 차이가 모두 있을 때뿐입니다.
     `data.before`·`data.after`만 보면, 두 창을 관측했지만 차이 계산이 실패한
     degraded 응답(예: invalid_ratio)이 "관측됨" 배지로 보입니다 — 서버가 미상이라고
     말한 것을 화면이 성공으로 뒤집으면 안 됩니다. */
  const hasDeltas = (data?.deltas.length ?? 0) > 0;
  const compared = section?.status === "ok" && Boolean(data?.before) && Boolean(data?.after) && hasDeltas;
  /* 한쪽만 성공한 창의 관측은 **남깁니다**(서버가 의도적으로 보존한 값입니다).
     다만 그것으로 비교 결론을 내지는 않습니다. */
  const hasAnySide = Boolean(data?.before) || Boolean(data?.after);
  const severity = compared ? "healthy" : "unknown";
  const stateLabel = compared ? t("common.rolloutCompare.observed") : t("common.rolloutCompare.unknownTitle");
  const deltaOf = (metric: RolloutCompareDelta["metric"]) => data?.deltas.find((d) => d.metric === metric);

  return (
    <Panel
      title={t("common.rolloutCompare.title")}
      section={section}
      referenceIso={referenceIso}
      className="rollout-compare-panel"
      outage={outage}
      actions={
        anchors.length > 0 ? (
          <>
            <label className="field" data-testid="rollout-anchor-field">
              <span className="field__label">{t("common.rolloutCompare.anchor")}</span>
              <select
                data-testid="rollout-anchor-select"
                value={selectedAnchor}
                onChange={(e) => choose("compareAnchor", e.target.value)}
              >
                {/* 빈 옵션은 "고르지 않음"이며 서버가 anchor_not_selected로 답합니다. */}
                <option value="">{t("common.rolloutCompare.anchorNone")}</option>
                {anchors.map((a) => {
                  const note = a.reason ? ANCHOR_NOTE[a.reason] : undefined;
                  return (
                    /* value는 **서버가 발급한 anchorId**입니다. revision이 아닙니다. */
                    <option key={a.anchorId} value={a.anchorId} disabled={!a.selectable}>
                      {t("common.rolloutCompare.anchorOption", {
                        revision: a.revision,
                        at: a.anchorAt ? clock(a.anchorAt) : t("common.rolloutCompare.anchorNoTime"),
                      })}
                      {a.current ? ` · ${t("common.rolloutCompare.anchorCurrent")}` : ""}
                      {note ? ` · ${t(note)}` : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="field" data-testid="rollout-window-field">
              <span className="field__label">{t("common.rolloutCompare.window")}</span>
              <select
                data-testid="rollout-window-select"
                value={selectedWindow}
                onChange={(e) => choose("compareWindow", e.target.value)}
              >
                <option value="">{t("common.rolloutCompare.windowDefault")}</option>
                {WINDOW_OPTIONS.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : undefined
      }
    >
      <div className="grid" data-testid="rollout-compare-card">
        <div className="row">
          <StatusBadge severity={severity} label={stateLabel} />
          {data?.selectedRevision && (
            <span className="muted" data-testid="rollout-selected">
              {t("common.rolloutCompare.selectedNote", { revision: data.selectedRevision })}
            </span>
          )}
        </div>

        {/* KPI 타일은 **비교가 성사되었을 때만** 보여 줍니다 — 차이가 없는데
            타일을 그리면 없는 결론이 있는 것처럼 읽힙니다. */}
        {compared && (
          <div className="grid grid--kpi">
            {data!.deltas.map((d) => (
              <StatTile
                key={d.metric}
                label={t(METRIC_MESSAGE[d.metric])}
                value={deltaText(d)}
                footnote={
                  d.relativeDefined
                    ? t("common.rolloutCompare.relative", { value: signed(ratioToPoints(d.relative ?? 0), d.relative ?? 0) })
                    : t("common.rolloutCompare.relativeUndefined")
                }
              />
            ))}
          </div>
        )}

        {hasAnySide && (
          <>

            {/* 두 창의 값을 **나란히** 보여 줍니다 — 차이만 보여 주면 규모를 알 수 없습니다. */}
            {/* 행은 **고정 지표 목록**에서 나옵니다. `deltas`에서 뽑으면 한쪽 창만
                성공했을 때 표가 통째로 사라져, 서버가 일부러 남긴 관측이 화면에서
                지워집니다. */}
            <table className="table" data-testid="rollout-delta">
              <caption className="visually-hidden">{t("common.rolloutCompare.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("common.rolloutCompare.metricHeader")}</th>
                  <th scope="col">{t("common.rolloutCompare.beforeHeader")}</th>
                  <th scope="col">{t("common.rolloutCompare.afterHeader")}</th>
                  <th scope="col">{t("common.rolloutCompare.deltaHeader")}</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map((metric) => {
                  const d = deltaOf(metric);
                  return (
                    <tr key={metric}>
                      <th scope="row">{t(METRIC_MESSAGE[metric])}</th>
                      <td className="num" data-testid={`rollout-before-${metric}`}>
                        {sideValue(data?.before, metric)}
                      </td>
                      <td className="num" data-testid={`rollout-after-${metric}`}>
                        {sideValue(data?.after, metric)}
                      </td>
                      <td className="num" data-testid={`rollout-delta-${metric}`}>
                        {d ? deltaText(d) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!compared && (
              /* 차이를 내지 않았다는 **사실**만 적습니다. 한쪽 관측으로 결론을
                 내지 않습니다. */
              <p className="muted" data-testid="rollout-no-delta">
                {t("common.rolloutCompare.noDelta")}
              </p>
            )}

            <div className="facts">
              <div className="fact">
                <span className="fact__label">{t("common.rolloutCompare.beforeHeader")}</span>
                <span className="fact__value" data-testid="rollout-before">
                  {data?.before
                    ? t("common.rolloutCompare.population", {
                        series: num(data.before.population.observedSeries),
                        pods: num(data.before.population.observedPods),
                        eligible: num(data.before.population.eligibleEvents),
                      })
                    : t("common.rolloutCompare.sideMissing")}
                </span>
              </div>
              <div className="fact">
                <span className="fact__label">{t("common.rolloutCompare.afterHeader")}</span>
                <span className="fact__value" data-testid="rollout-after">
                  {data?.after
                    ? t("common.rolloutCompare.population", {
                        series: num(data.after.population.observedSeries),
                        pods: num(data.after.population.observedPods),
                        eligible: num(data.after.population.eligibleEvents),
                      })
                    : t("common.rolloutCompare.sideMissing")}
                </span>
              </div>
              {/* 코호트 관계는 **두 창을 모두 관측했을 때만** 뜻이 있습니다. */}
              {compared && (
                <div className="fact">
                  <span className="fact__label">{t("common.rolloutCompare.cohortLabel")}</span>
                  <span className="fact__value" data-testid="rollout-cohort">
                    {t("common.rolloutCompare.cohort", {
                      both: num(data!.seriesBoth),
                      onlyBefore: num(data!.seriesOnlyBefore),
                      onlyAfter: num(data!.seriesOnlyAfter),
                    })}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {window && (
          /* 네 경계와 제외 구간을 **그대로** 보여 줍니다 — 버려진 구간을 숨기면
             "관측 없음"이 "변화 없음"처럼 보입니다. */
          <p className="muted" data-testid="rollout-window-detail">
            {t("common.rolloutCompare.windowDetail", {
              beforeStart: clock(window.beforeStart),
              beforeEnd: clock(window.beforeEnd),
              afterStart: clock(window.afterStart),
              afterEnd: clock(window.afterEnd),
              gap: windowLabel(window.excludedGapSeconds),
              length: windowLabel(window.seconds),
            })}
            {window.clamped && ` · ${t("common.rolloutCompare.windowClamped", { requested: window.requestedName })}`}
          </p>
        )}

        {/* 한계는 값이 있든 없든 **항상** 노출합니다. */}
        {data && data.limitations.length > 0 && (
          <ul className="muted" data-testid="rollout-limitations">
            {data.limitations.map((l) => (
              <li key={l}>{t(LIMITATION_MESSAGE[l])}</li>
            ))}
          </ul>
        )}

        {reason && (
          <div
            className={`state${outage ? " state--degraded" : ""}`}
            data-testid="rollout-unknown"
            data-outage={outage ? "true" : "false"}
          >
            <span className="state__glyph" aria-hidden="true">
              {outage ? "!" : "·"}
            </span>
            <span className="state__title">{t("common.rolloutCompare.unknownTitle")}</span>
            <span className="state__detail" data-testid="rollout-reason">
              {WINDOW_OF[reason]
                ? t(REASON_MESSAGE[reason], { window: t(WINDOW_OF[reason]!) })
                : t(REASON_MESSAGE[reason])}
            </span>
            {!outage && (
              <span className="muted" data-testid="rollout-not-outage">
                {t("common.rolloutCompare.notAnOutage")}
              </span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
